import React, { useState, useCallback } from "react";
import "./PageFeedbackCard.css";

const REASONS = [
  { id: "not_useful", label: "Information not useful" },
  { id: "design", label: "Design not attractive" },
];

async function postFeedback(payload) {
  const res = await fetch("/page-feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Feedback failed: ${res.status}`);
  return res.json();
}

/**
 * BNR-style page feedback: Yes sends positive signal; No opens reasons then submits.
 */
export default function PageFeedbackCard({ pagePath }) {
  const path = pagePath || (typeof window !== "undefined" ? window.location.pathname : "/Publications");

  const [phase, setPhase] = useState("idle"); // idle | negative | sending | done | error
  const [reasonIds, setReasonIds] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  const toggleReason = useCallback((id) => {
    setReasonIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleYes = useCallback(async () => {
    setErrorMsg("");
    setPhase("sending");
    try {
      await postFeedback({
        page: path,
        satisfied: true,
        reasons: [],
        comment: "",
      });
      setPhase("done");
    } catch {
      setPhase("error");
      setErrorMsg("Could not send feedback. Please try again later.");
    }
  }, [path]);

  const handleNoClick = useCallback(() => {
    setErrorMsg("");
    setPhase("negative");
    setReasonIds([]);
  }, []);

  const handleNegativeSubmit = useCallback(async () => {
    if (reasonIds.length === 0) {
      setErrorMsg("Please select at least one option.");
      return;
    }
    setErrorMsg("");
    setPhase("sending");
    try {
      await postFeedback({
        page: path,
        satisfied: false,
        reasons: reasonIds,
        comment: "",
      });
      setPhase("done");
    } catch {
      setPhase("error");
      setErrorMsg("Could not send feedback. Please try again later.");
    }
  }, [path, reasonIds]);

  const handleCancelNegative = useCallback(() => {
    setPhase("idle");
    setReasonIds([]);
    setErrorMsg("");
  }, []);

  if (phase === "done") {
    return (
      <div className="service-card">
        <div className="service-card__content">
          <div className="service-card__content__inner">
            <h4 className="service-card__title">Thank you!</h4>
            <p className="mt-3 mb-0">Your feedback has been recorded and helps us improve this site.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-card page-feedback-card">
      <div className="service-card__content">
        <div className="service-card__content__inner">
          <h4 className="service-card__title">Are you happy with this page?</h4>

          {phase === "idle" && (
            <>
              <div className="d-flex gap-2 mt-3 flex-wrap align-items-center">
                <button type="button" className="thm-btn" onClick={handleYes} disabled={phase === "sending"}>
                  Yes
                </button>
                <button type="button" className="thm-btn" onClick={handleNoClick}>
                  No
                </button>
              </div>
              <p className="mt-3 mb-0 small text-muted">Your feedback helps us improve structure and discoverability.</p>
            </>
          )}

          {phase === "negative" && (
            <div className="mt-2">
              <p className="mt-3 mb-2">What made you unhappy?</p>
              <ul className="list-unstyled mb-3">
                {REASONS.map(({ id, label }) => (
                  <li key={id} className="mb-1">
                    <label className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                      <input type="checkbox" checked={reasonIds.includes(id)} onChange={() => toggleReason(id)} />
                      <span>{label}</span>
                    </label>
                  </li>
                ))}
              </ul>
              {errorMsg ? <p className="text-danger small mb-2 mb-md-3">{errorMsg}</p> : null}
              <div className="d-flex flex-row flex-wrap gap-2 justify-content-end align-items-center page-feedback-card__actions">
                <button type="button" className="thm-btn thm-btn--compact" onClick={handleNegativeSubmit}>
                  Submit
                </button>
                <button type="button" className="thm-btn thm-btn--compact" onClick={handleCancelNegative}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {phase === "sending" && (
            <p className="mt-3 mb-0" aria-busy="true">
              Sending…
            </p>
          )}

          {phase === "error" && (
            <>
              <p className="mt-3 mb-2 text-danger">{errorMsg}</p>
              <div className="d-flex gap-2 flex-wrap">
                <button type="button" className="thm-btn thm-btn--compact" onClick={() => setPhase("idle")}>
                  Try again
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
